import React, { useState } from 'react';
import { ReactFlowFamilyTree } from './ReactFlowFamilyTree';
import { LinkMemberModal } from './modals/LinkMemberModal';
import { useFamilyStore } from '../../store/familyStore';
import type { FamilyMember } from '../../types/family';

export const ReactFlowTreeView: React.FC = () => {
  const { members, addMember, addMemberWithRelationship, updateMember, deleteMember, viewMode } = useFamilyStore();
  const [linkTargetId, setLinkTargetId] = useState<string | null>(null);

  const filteredMembers = members.filter(member => {
    if (!viewMode.showAlive && member.isAlive) return false;
    if (!viewMode.showDeceased && !member.isAlive) return false;
    if (viewMode.selectedGeneration && member.generation !== viewMode.selectedGeneration) return false;
    return true;
  });

  const handleMemberUpdate = (updatedMember: FamilyMember) => {
    updateMember(updatedMember.id, updatedMember);
  };

  const handleMemberAdd = (newMember: Partial<FamilyMember>) => {
    const member = {
      id: `member-${Date.now()}`,
      name: 'New Member',
      birthDate: new Date().toISOString().split('T')[0],
      gender: 'male' as const,
      isAlive: true,
      generation: 1,
      maritalStatus: 'single' as const,
      ...newMember,
    };

    // If the add came from a right-click "Add Child/Spouse", link the
    // new member to the target via a real relationship record.
    if (newMember.parentIds && newMember.parentIds.length > 0) {
      addMemberWithRelationship(member, 'biological_child', newMember.parentIds[0]);
      return;
    }

    if (newMember.spouseId) {
      addMemberWithRelationship(member, 'partner', newMember.spouseId);
      return;
    }

    addMember(member);
  };

  const handleMemberDelete = (memberId: string) => {
    deleteMember(memberId);
  };

  const handleLinkMember = (memberId: string) => {
    setLinkTargetId(memberId);
  };

  return (
    <div className="w-full h-full">
      <ReactFlowFamilyTree
        members={filteredMembers}
        onMemberUpdate={handleMemberUpdate}
        onMemberAdd={handleMemberAdd}
        onMemberDelete={handleMemberDelete}
        onLinkMember={handleLinkMember}
      />
      <LinkMemberModal
        isOpen={linkTargetId !== null}
        memberId={linkTargetId ?? ''}
        onClose={() => setLinkTargetId(null)}
      />
    </div>
  );
};
